import { RequestTypeEnum } from "@nlc/shared-types";
import { useEffect, useState } from "react";

import { MOCKED_GRAPHIC_CHAT_STORIES } from "@/features/templates/mocks/chatStories.ts";
import { MOCKED_VIDEO_CHAT_STORIES } from "@/features/templates/mocks/videoChatStories.ts";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";

import type { ChatStory } from "../types";
import type { StatusAction } from "../utils/message-parsers";

import { useChatStatusActions } from "./useChatStatusActions";

interface UseChatDataEventParams {
  requestId: number;
}

interface UseChatDataEventReturn {
  error: Error | null;
  isLoading: boolean;
  stories: ChatStory[];
}

function isStatusAction(data: unknown): data is StatusAction {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    "data" in data &&
    typeof (data as StatusAction).type === "string" &&
    [
      "all_attachments_deleted",
      "attachment_deleted",
      "message_deleted",
      "message_edited",
    ].includes((data as StatusAction).type)
  );
}

export function useChatDataEvent({
  requestId,
}: UseChatDataEventParams): UseChatDataEventReturn {
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [stories, setStories] = useState<ChatStory[]>([]);

  const { handleStatusAction } = useChatStatusActions();

  const { isTemplate, requestType } = useIsTemplate();

  useEffect(() => {
    if (isTemplate) {
      setIsLoading(false);
      setError(null);
      if (requestType === RequestTypeEnum.GRAPHIC)
        setStories(MOCKED_GRAPHIC_CHAT_STORIES as ChatStory[]);
      else setStories(MOCKED_VIDEO_CHAT_STORIES as ChatStory[]);

      return;
    }

    setIsLoading(true);
    setStories([]);
    setError(null);

    const eventSource = new EventSource(
      `${window.location.origin}/api/v1/stories/chat/${requestId}`,
      { withCredentials: true },
    );

    const handleMessage = (event: MessageEvent) => {
      try {
        const eventData = JSON.parse(event.data);

        if (isStatusAction(eventData)) {
          setStories((prevStories) => {
            const updatedStories = handleStatusAction(prevStories, eventData);

            return updatedStories as ChatStory[];
          });
        } else {
          const newStory = eventData as ChatStory;

          setStories((prevStories) => {
            // Prevent duplicates by checking if story already exists
            const existingStory = prevStories.find(
              (story) => story.id === newStory.id,
            );

            if (existingStory) {
              return prevStories;
            }

            return [...prevStories, newStory];
          });
        }
      } catch (error_) {
        console.error("Error parsing event data:", error_);

        setError(
          error_ instanceof Error
            ? error_
            : new Error("Unknown error parsing data"),
        );
      }
    };

    const handleError = (event: Event) => {
      // Check if it's just a cosmetic chunked encoding warning
      if (eventSource.readyState === EventSource.OPEN) {
        // Connection is still open, just ignore the warning
        return;
      }

      console.warn("Real connection error, reconnecting...", event);

      setError(new Error("Connection error, attempting to reconnect"));
    };

    const handleOpen = () => {
      setIsLoading(false);

      setError(null);
    };

    eventSource.addEventListener("message", handleMessage);
    eventSource.addEventListener("error", handleError);
    eventSource.addEventListener("open", handleOpen);

    return () => {
      eventSource.close();
    };
  }, [requestId, handleStatusAction]);

  return { error, isLoading, stories };
}
